/**
 * Edit Delta Component Factory Function
 * Used to generate edit-delta components for each plan, avoiding code duplication
 * 
 * @param {Object} options - Configuration options
 * @param {string} options.name - Component name
 * @param {string} options.planType - Plan type (from planTypes)
 * @param {Object} options.mixin - Plan-specific mixin
 * @param {boolean} [options.withReplenish=false] - Whether to include replenish mixin for auto add/remove base plan
 * @param {string} [options.className='opc-zwb'] - Custom CSS class name
 * @param {boolean} [options.hideConfigDescription=false] - Whether to hide the config description
 * @returns {Object} Vue component definition
 */
import plansMixin from '@/plans/mx/plan';
import ConfigBase from '@/components/config-base.vue';
import PreviewPayment from '@/views/checkout/payment/preview-payment.vue';
import DeltaEditMain from '@/components/delta-edit-main.vue';
import { ServiceConfirm } from '@/components/common/service-dialog/confirm';
import ViewCurrentDialog from '@/components/view-current-dialog/index.vue';
import replenishMixin from '@/components/capacity/mx/replenish';
import { mapGetters } from 'vuex';
import { planTypes } from '@/common/constants';

export function createEditDeltaComponent({ 
  name, 
  planType, 
  mixin, 
  withReplenish = false,
  className = 'opc-zwb',
  hideConfigDescription = false,
}) {
  const ConfigBaseComp = {
    extends: ConfigBase,
    key: planTypes[planType],
    mixins: [mixin],
    props: {
      showPlanDetails: Boolean,
      planDetailsLabel: String,
    },
    data() {
      const configBaseData = {
        showLearnMore: false,
      };
      
      // Hide config description if needed (must be here because ConfigBase uses it)
      if (hideConfigDescription) {
        configBaseData.configDescription = '';
      }
      
      return configBaseData;
    },
  };

  // Build mixins array based on options
  const mixins = [plansMixin, mixin];
  if (withReplenish) {
    mixins.push(replenishMixin);
  }

  return {
    name,
    mixins,
    components: {
      DeltaEditMain,
      PreviewPayment,
      ConfigBase: ConfigBaseComp,
    },
    data() {
      const baseData = {
        viewCurrentDialog: null,
      };
      
      // Add msgInstance if replenish is enabled
      if (withReplenish) {
        baseData.msgInstance = null;
      }
      
      return baseData;
    },
    computed: {
      ...mapGetters(['getSelectedPlans']),
      selectedPlanList() {
        return this.getSelectedPlans;
      },
    },
    methods: {
      handleViewDetails() {
        this.viewCurrentDialog.confirm({
          planType: this.planType,
        });
      },
      ...(withReplenish ? {
        showMessage(message) {
          this.msgInstance && this.msgInstance.close();
          this.msgInstance = this.$message({
            type: 'info',
            duration: 8000,
            message: message,
            showClose: true,
          });
        },
        mx_onAddedBasePlan(basePlanName) {
          this.showMessage(this.$t('billing.addon_auto_add_tips', [this.title, basePlanName]));
        },
        mx_onRemovedBasePlan(basePlanName) {
          this.showMessage(this.$t('billing.addon_auto_remove_tips', [this.title, basePlanName]));
        },
      } : {}),
    },
    created() {
      this.viewCurrentDialog = new ServiceConfirm(ViewCurrentDialog);
    },
    render() {
      const baseRefProps = withReplenish ? { ref: 'baseRef' } : {};
      const listeners = withReplenish ? { on: { ...this.$listeners, 'view-details': this.handleViewDetails } } : { on: { 'view-details': this.handleViewDetails } };
      
      return (
        <config-base 
          class={className}
          {...baseRefProps}
          {...listeners}
          show-plan-details 
          plan-details-label={this.$t('billing.delta.cur_plan_detail')}
        >
          <template slot="config">
            <delta-edit-main planType={this.planType} title={this.title} />
            <preview-payment class={`${className}__preview-payment`} />
          </template>
        </config-base>
      );
    },
  };
}

